import { cloneDeep, omit } from 'lodash';
import { trackConfig, type FormatBrushApplyContext, type IFramework } from '@/framework';
import { isHotKeyPress } from '@/utils/event-kit';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { isPositiveFiniteNumber } from '@/utils/math-kit';
import { LayerType, MaterialIdsType, TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { type FormatBrushData } from '../types';
import {
    applySnapshotSize,
    collectFontSubsetRequests,
    getLayerExtraInfo,
    pushHistory,
    refreshFontMaterialIDs,
    refreshRuntimeFontFamilies,
    updateLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';
import { type IArtText } from './art-text';
import { type IShapeText } from './shape-text';

export interface IFormatBrush {
    /**
     * 提取格式刷数据
     * @param layerID - 图层 id
     * @returns
     */
    extractFormatBrushData(layerID: string): FormatBrushData | undefined;
    /**
     * 应用格式刷
     * @param layerID - 图层 id
     * @returns
     */
    applyFormatBrush(
        layerID: string,
        context: FormatBrushApplyContext,
        formatBrushData?: FormatBrushData,
    ): Promise<ILayerProps | void>;

    /**
     * 清理当前提取的格式数据。
     * @param formatBrushData - 格式数据
     */
    clearFormatBrushData(formatBrushData?: FormatBrushData): void;
}

export class FormatBrush implements IFormatBrush {
    private formatBrushData: FormatBrushData | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
        private artText: IArtText,
    ) {}

    extractFormatBrushData(layerID: string) {
        const layer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!layer || layer.layerType !== LayerType.TEXT_LAYER) return;

        const { opacity, materialIds } = layer;
        const layerData = layer.data as ITextLayerProps;
        const {
            bold,
            color,
            fontFamily,
            fontSize,
            italic,
            letterSpacing,
            lineSpacing,
            lineThrough,
            listStyle,
            listStyleMaterial,
            textAlign,
            textBackground,
            textGlow,
            textGradient,
            textShadow,
            textStroke,
            textMark,
            underline,
            writingMode,
            shapeExtension,
            texture,
            advancedTextMaterial,
        } = layerData;

        const formatBrushData: FormatBrushData = {
            materialIds,
            opacity,
            data: {
                bold,
                color,
                fontFamily,
                fontSize,
                italic,
                letterSpacing,
                lineSpacing,
                lineThrough,
                listStyle,
                listStyleMaterial,
                textAlign,
                textBackground,
                textGlow,
                textGradient,
                textShadow,
                textStroke,
                textMark,
                underline,
                writingMode,
                shapeExtension,
                texture,
                advancedTextMaterial,
            },
        };

        if (advancedTextMaterial) {
            const extraInfo = getLayerExtraInfo(layer);

            if (extraInfo.overrideColorInArtTextCase) {
                formatBrushData.__overrideColorInArtTextCase = extraInfo.overrideColorInArtTextCase;
            } else if (layerData.textContents && layerData.textContents.length > 0) {
                const textContentColors = layerData.textContents.map((item) => item.color);
                const colorSet = new Set(textContentColors);
                if (colorSet.size === 1) {
                    formatBrushData.__overrideColorInArtTextCase = textContentColors[0];
                }
            }
        }

        this.formatBrushData = formatBrushData;

        return this.formatBrushData;
    }

    clearFormatBrushData(formatBrushData = this.formatBrushData ?? undefined) {
        if (this.formatBrushData === formatBrushData) this.formatBrushData = null;
    }

    private async prepareFontSubsets(layer: ILayerProps): Promise<boolean> {
        const requests = collectFontSubsetRequests(layer);
        if (requests.length === 0) return true;

        const results = await Promise.all(
            requests.map(async ({ fontFamily, chars }) => {
                if (!this.framework.service.font.getByFamily(fontFamily)) return false;
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return false;

                setLayerRuntimeValue(layer, {
                    fontFamilyMap: {
                        ...(layer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return true;
            }),
        );

        return results.every(Boolean);
    }

    /**
     * 为格式刷候选校正最小文字框尺寸，并完成最终排版
     *
     * 格式刷继承目标文字框的尺寸约束，但新字号、字体或字距可能使原框连一个不可断开的
     * 排版片段都容纳不下。例如框宽 40 时应用字号 130，单个汉字的排版宽度可能约为 128；
     * 即使逐字换行，仍会溢出。applySnapshotSize 按约定保留固定行内尺寸，不能替此入口校验。
     *
     * 因此先按目标原约束排版，只在固定行内尺寸不足时扩容到引擎给出的最小值，再重新排版。
     * 自适应文字框和尺寸足够的固定框沿用原规则，不复制源图层宽度，也不按整段文字撑宽。
     * 返回值保留目标文字框的中心，调用方仅在准备成功后一起提交样式、尺寸与历史记录。
     *
     * @param prevLayer - 应用前的目标图层，用于保留尺寸约束和定位基准
     * @param layer - 已写入源文字样式、尚未提交的目标候选图层
     * @returns 与最终排版匹配的候选图层；字体或任一轮排版未就绪时返回 null，不提交半成品
     */
    private async prepareLayout(prevLayer: ILayerProps, layer: ILayerProps) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData = layer.data as ITextLayerProps;
        let nextLayer = layer;

        if (prevLayer.__fixedWidth !== false && prevLayerData.writingMode !== nextLayerData.writingMode) {
            const previousInlineSize =
                prevLayerData.writingMode === TextWritingType.HORIZONTAL_TB ? prevLayer.width : prevLayer.height;
            nextLayer =
                nextLayerData.writingMode === TextWritingType.HORIZONTAL_TB
                    ? { ...nextLayer, width: previousInlineSize }
                    : { ...nextLayer, height: previousInlineSize };
        }

        nextLayer = refreshRuntimeFontFamilies(nextLayer);
        if (!(await this.prepareFontSubsets(nextLayer))) return null;

        const host = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id);
        let resolution = host?.layout(nextLayer);
        // 格式与几何需要原子提交，非 ready 候选直接放弃。
        if (resolution?.status !== 'ready') return null;

        let snapshot = resolution.value;
        // __fixedWidth 表示固定行内尺寸：横排约束宽度，竖排约束高度。
        const inlineAxis = snapshot.writingMode === 'horizontal-tb' ? 'width' : 'height';

        // minInlineSize 是新样式下不可断开片段的最小占用，不等于字号或当前整行宽度。
        // 引擎正文约束已扣除列表标记占用，恢复到完整文字框尺寸时需要加回同轮测得的缩进。
        const minInlineSize = snapshot.minInlineSize + snapshot.listMarkerIndent;
        if (
            nextLayer.__fixedWidth !== false &&
            isPositiveFiniteNumber(minInlineSize) &&
            nextLayer[inlineAxis] < minInlineSize
        ) {
            // 只扩到合法下限，保留固定文字框的换行语义；不修改原目标图层。
            nextLayer = { ...nextLayer, [inlineAxis]: minInlineSize };
            // 宽度变化会影响对齐位置，并可能改变混合字符的换行与高度。
            // 不能把扩大后的框与旧约束下的快照拼接提交，即使本次行数看起来没有变化。
            resolution = host?.layout(nextLayer);
            // 第二轮也必须 ready，否则整个格式刷操作放弃，不能只提交新样式或扩容后的框。
            if (resolution?.status !== 'ready') return null;
            snapshot = resolution.value;
        }

        nextLayer = applySnapshotSize(nextLayer, snapshot);

        return {
            ...nextLayer,
            left: prevLayer.left + (prevLayer.width - nextLayer.width) / 2,
            top: prevLayer.top + (prevLayer.height - nextLayer.height) / 2,
        };
    }

    async applyFormatBrush(
        layerID: string,
        _: FormatBrushApplyContext,
        formatBrushData = this.formatBrushData ?? undefined,
    ) {
        if (formatBrushData == null) return;

        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const { opacity } = formatBrushData;

        let materialIds = cloneDeep(formatBrushData.materialIds);
        let data = cloneDeep(formatBrushData.data);

        // 如果目标格式刷数据包含花字特效，且与源图层的花字特效不一致，则直接走应用花字特效逻辑
        if (data.advancedTextMaterial) {
            if (
                prevLayerData.advancedTextMaterial &&
                prevLayerData.advancedTextMaterial.id === data.advancedTextMaterial.id
            ) {
                data = omit(data, 'advancedTextMaterial');
                if (materialIds) materialIds = omit(materialIds, MaterialIdsType.ADVANCED_TEXT_MATERIAL);
            } else {
                const nextFormatBrushData = cloneDeep(formatBrushData);

                this.formatBrushData = null;

                return this.artText.applyArtText(layerID, data.advancedTextMaterial, {
                    formatBrushData: nextFormatBrushData,
                });
            }
        }

        const nextLayerData: ITextLayerProps = { ...prevLayerData, ...data };

        let nextLayer: ILayerProps = {
            ...prevLayer,
            opacity,
            materialIds,
            data: nextLayerData,
        };

        // 需要校验真实使用的字体，来确认最终的素材 ID，防止前一步操作添加了多余的字体素材 ID
        // 比如源图层中有部分字体使用了 vip 字体，但是应用格式刷时并不会使用 textContents 中的 vip 字体
        // 但是源图层的 materialIds 中却有 vip 字体的素材 ID
        // 故需要重新计算字体素材 ID
        nextLayer = refreshFontMaterialIDs(this.framework.service.font, nextLayer);

        const { shapeExtension } = nextLayerData;
        if (!shapeExtension) {
            const prepared = await this.prepareLayout(prevLayer, nextLayer);
            if (!prepared) return;

            nextLayer = prepared;
        } else {
            const layer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (layer) nextLayer = layer;
        }

        if (isHotKeyPress()) {
            this.framework.service.frame.addOrUpdateLayer(nextLayer);
        } else {
            updateLayer(this.framework, nextLayer);
            if (this.formatBrushData === formatBrushData) this.formatBrushData = null;
        }
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].copyBrush);

        return nextLayer;
    }
}
